import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Card,
    CardContent,
    Avatar,
    CircularProgress
} from '@mui/material';
import {
    Search,
    Clear,
    Visibility,
    Person,
    Business,
    DirectionsCar,
} from '@mui/icons-material';
import { COLORS } from '../../settings/themeSettings';
import searchService from '../../services/searchService';
import { useNavigate } from 'react-router-dom';

const RechercheUnifiee = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        module: 'tous',
        dateDebut: '',
        dateFin: '',
        statut: 'tous',
        type: 'tous'
    });
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);

    const modules = [
        { value: 'tous', label: 'Tous les modules' },
        { value: 'clients', label: 'Clients' },
        { value: 'circuits', label: 'Circuits' },
        { value: 'factures', label: 'Factures' },
    ];

    const statuts = [
        { value: 'tous', label: 'Tous les statuts' },
        { value: 'actif', label: 'Actif' },
        { value: 'payée', label: 'Payée' },
        { value: 'en attente', label: 'En attente' },
        { value: 'annulé', label: 'Annulé' }
    ];

    const handleSearch = async () => {
        if (!searchTerm) return;
        setLoading(true);
        setSearched(true);
        try {
            const data = await searchService.globalSearch(searchTerm, filters.module, filters);
            setResults(data);
        } catch (error) {
            console.error("Search error", error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setFilters({
            module: 'tous',
            dateDebut: '',
            dateFin: '',
            statut: 'tous',
            type: 'tous'
        });
        setResults([]);
        setSearched(false);
    };

    const getStatusColor = (statut) => {
        if (!statut) return 'default';
        switch (statut.toString().toLowerCase()) {
            case 'actif':
            case 'payée':
                return 'success';
            case 'en cours':
            case 'en attente':
                return 'warning';
            case 'annulé':
                return 'error';
            default:
                return 'default';
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case 'client': return Person;
            case 'circuit': return DirectionsCar;
            case 'facture': return Business;
            default: return Search;
        }
    };

    const handleNavigate = (item) => {
        switch (item.type) {
            // Adaptez les routes selon votre structure
            case 'client': navigate(`/billeterie/portefeuil`); break; // Idéalement pointer vers /client/:id
            case 'circuit': navigate(`/ajout/dossier/circuit`); break;
            case 'facture': navigate(`/facturation/unifiee`); break;
            default: break;
        }
    }

    return (
        <Box sx={{ padding: 3 }}>
            <Paper sx={{ padding: 3, marginBottom: 3 }}>
                <Typography variant="h4" sx={{
                    color: COLORS.secondary,
                    fontWeight: 'bold',
                    marginBottom: 2,
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    <Search sx={{ marginRight: 2, fontSize: 35 }} />
                    Recherche Unifiée
                </Typography>

                <Typography variant="body1" sx={{ color: '#666', marginBottom: 3 }}>
                    Recherchez dans tous les modules de TravelManager avec des critères avancés.
                </Typography>

                {/* Barre de recherche principale */}
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
                    <TextField
                        fullWidth
                        // placeholder="Rechercher un client instantanément..." // Masqué pour l'instant
                        placeholder="Rechercher (Nom client, Numéro dossier, Réf facture...)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        InputProps={{
                            startAdornment: <Search sx={{ color: '#666', marginRight: 1 }} />
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSearch}
                        disabled={loading}
                        sx={{ minWidth: 120, backgroundColor: COLORS.secondary }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Rechercher'}
                    </Button>
                </Box>

                {/* Filtres avancés */}
                <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Module</InputLabel>
                            <Select
                                value={filters.module}
                                onChange={(e) => setFilters({ ...filters, module: e.target.value })}
                                label="Module"
                            >
                                {modules.map(module => (
                                    <MenuItem key={module.value} value={module.value}>
                                        {module.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Statut</InputLabel>
                            <Select
                                value={filters.statut}
                                onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
                                label="Statut"
                            >
                                {statuts.map(statut => (
                                    <MenuItem key={statut.value} value={statut.value}>
                                        {statut.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<Clear />}
                            onClick={handleClearFilters}
                            size="small"
                            color="error"
                        >
                            Effacer
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Résultats */}
            {results.length > 0 && (
                <Paper sx={{ padding: 3 }}>
                    <Typography variant="h6" sx={{
                        color: COLORS.secondary,
                        marginBottom: 2,
                        fontWeight: 'bold'
                    }}>
                        Résultats de recherche ({results.length})
                    </Typography>

                    <Grid container spacing={2}>
                        {results.map((result) => {
                            const Icon = getIcon(result.type);
                            return (
                                <Grid item xs={12} md={6} lg={4} key={`${result.type}-${result.id}`}>
                                    <Card sx={{
                                        height: '100%',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 4
                                        }
                                    }} onClick={() => handleNavigate(result)}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', marginBottom: 2 }}>
                                                <Avatar sx={{
                                                    backgroundColor: COLORS.secondary,
                                                    marginRight: 2,
                                                    width: 40,
                                                    height: 40
                                                }}>
                                                    <Icon />
                                                </Avatar>
                                                <Box sx={{ flexGrow: 1 }}>
                                                    <Typography variant="h6" sx={{
                                                        fontWeight: 'bold',
                                                        marginBottom: 0.5,
                                                        fontSize: '1rem'
                                                    }}>
                                                        {result.title}
                                                    </Typography>
                                                    <Chip
                                                        label={result.status}
                                                        color={getStatusColor(result.status)}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </Box>

                                            <Typography variant="body2" sx={{
                                                color: '#666',
                                                marginBottom: 2,
                                                minHeight: 40
                                            }}>
                                                {result.description}
                                            </Typography>

                                            <Box sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                borderTop: '1px solid #eee',
                                                paddingTop: 1
                                            }}>
                                                <Typography variant="caption" sx={{ color: '#999' }}>
                                                    {result.module} {result.date ? `• ${new Date(result.date).toLocaleDateString()}` : ''}
                                                </Typography>
                                                <Visibility color="action" fontSize="small" />
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Paper>
            )}

            {results.length === 0 && searched && !loading && (
                <Paper sx={{ padding: 4, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: '#666', marginBottom: 1 }}>
                        Aucun résultat trouvé
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#999' }}>
                        Aucune correspondance pour "{searchTerm}" avec les filtres actuels.
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default RechercheUnifiee;
